import {
  BookOpen24Regular,
  Bot24Regular,
  ChatMultiple24Regular,
  DataBarVertical24Regular,
  Flash24Regular,
  Megaphone24Regular,
  Money24Regular,
  People24Regular,
  PersonAdd24Regular,
  PlugConnected24Regular,
  Settings24Regular,
  TaskListLtr24Regular,
} from '@fluentui/react-icons';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { PRODUCT_NAME } from '@web/core/app-info';
import { getHelpSections } from '@web/modules/help/help-catalog';
import { HelpSearchInput } from '@web/modules/help/components/help-search-input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@web/shared/ui/card';
import { ScrollArea } from '@web/shared/ui/scroll-area';

export const Route = createFileRoute('/help/')({
  component: HelpIndexRoute,
});

const SECTION_ICONS: Record<string, typeof BookOpen24Regular> = {
  'getting-started': BookOpen24Regular,
  conversations: ChatMultiple24Regular,
  deals: Money24Regular,
  leads: PersonAdd24Regular,
  customers: People24Regular,
  tasks: TaskListLtr24Regular,
  campaigns: Megaphone24Regular,
  automations: Flash24Regular,
  channels: Bot24Regular,
  reports: DataBarVertical24Regular,
  settings: Settings24Regular,
  integrations: PlugConnected24Regular,
};

function HelpIndexRoute() {
  const { t } = useTranslation();
  const sections = getHelpSections().filter((s) => s.articles.length > 0);

  return (
    <ScrollArea className="h-full w-full">
      <div className="mx-auto max-w-5xl space-y-10 px-4 py-8 sm:px-6">
        {/* Hero Header */}
        <div className="rounded-2xl border border-brand-stroke/30 bg-gradient-to-b from-brand-tint/40 to-background p-8 text-center sm:p-12">
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-4xl">
            {t('help.heroTitle', 'Como podemos ajudar você hoje?')}
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">
            {t(
              'help.heroSubtitle',
              'Encontre tutoriais, guias práticos e respostas para usar todo o potencial do {{product}}.',
              { product: PRODUCT_NAME },
            )}
          </p>

          <div className="mx-auto mt-6 max-w-lg">
            <HelpSearchInput
              placeholder={t('help.searchPlaceholderHero', 'Pesquisar tutoriais (ex: WhatsApp, funil, automações)...')}
            />
          </div>
        </div>

        {/* Categories Grid */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold tracking-tight text-foreground">
            {t('help.categoriesTitle', 'Navegue por tópicos')}
          </h2>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sections.map((section) => {
              const Icon = SECTION_ICONS[section.id] ?? BookOpen24Regular;
              return (
                <Card key={section.id} className="flex flex-col justify-between transition-colors hover:border-brand/40">
                  <CardHeader className="space-y-2">
                    <div className="flex size-10 items-center justify-center rounded-lg bg-brand-tint text-brand-fg">
                      <Icon className="size-5 text-brand" />
                    </div>
                    <CardTitle className="text-base font-semibold">
                      {t(section.titleKey, section.id)}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {t('help.articleCount', '{{count}} artigos disponíveis', {
                        count: section.articles.length,
                      })}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2 pt-0">
                    <ul className="space-y-1.5 border-t border-border/50 pt-3">
                      {section.articles.slice(0, 3).map((article) => (
                        <li key={article.slug}>
                          <Link
                            to="/help/$"
                            params={{ _splat: article.slug }}
                            className="flex items-center text-xs text-foreground/80 hover:text-brand hover:underline"
                          >
                            <span className="line-clamp-1">{article.frontmatter.title}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>

                    {section.articles[0] && (
                      <div className="pt-2">
                        <Link
                          to="/help/$"
                          params={{ _splat: section.articles[0].slug }}
                          className="inline-flex items-center text-xs font-medium text-brand hover:underline"
                        >
                          {t('help.viewCategory', 'Ver todos →')}
                        </Link>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}
